import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Mail, CheckCircle, Loader2 } from "lucide-react";
import { invokeWithBackoff } from "@/lib/invokeWithBackoff";
import { useI18n } from "@/contexts/I18nContext";

interface NewsletterFormProps {
  source?: string;
  className?: string;
  placeholder?: string;
  buttonLabel?: string;
  successMessage?: string;
}

export default function NewsletterForm({
  source = "site",
  className = "",
  placeholder,
  buttonLabel,
  successMessage,
}: NewsletterFormProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [hpWebsite, setHpWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const resolvedPlaceholder = placeholder ?? t("site.newsletter.placeholder");
  const resolvedButton = buttonLabel ?? t("site.newsletter.button");
  const resolvedSuccess = successMessage ?? t("site.newsletter.success");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    // H5: honeypot — bots fill hidden field; silently accept and skip send.
    if (hpWebsite) {
      setStatus("success");
      return;
    }
    setStatus("loading");
    setErrorMsg("");

    const { error } = await invokeWithBackoff("newsletter-signup", {
      email: email.trim().toLowerCase(),
      source,
      hp_website: hpWebsite,
    });
    if (error) {
      const msg = error instanceof Error ? error.message : "error";
      setErrorMsg(msg.includes("rate_limit") ? t("site.newsletter.error_rate") : t("site.newsletter.error"));
      setStatus("error");
      return;
    }
    setStatus("success");
    setEmail("");
  }

  if (status === "success") {
    return (
      <m.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center gap-2 text-emerald-400 text-sm ${className}`}
      >
        <CheckCircle className="w-4 h-4 shrink-0" />
        <span>{resolvedSuccess}</span>
      </m.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`flex gap-2 flex-wrap ${className}`}>
      <input
        type="text"
        name="hp_website"
        tabIndex={-1}
        autoComplete="off"
        value={hpWebsite}
        onChange={(e) => setHpWebsite(e.target.value)}
        className="sr-honeypot"
        aria-hidden="true"
      />
      <div className="relative flex-1 min-w-[200px]">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
        <input
          type="email" autoComplete="email" inputMode="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={resolvedPlaceholder}
          aria-label={resolvedPlaceholder}
          required
          className="w-full bg-card/60 border border-border/60 rounded-full pl-9 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/60 transition"
        />
      </div>
      <button
        type="submit"
        disabled={status === "loading"}
        className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity shrink-0"
      >
        {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {resolvedButton}
      </button>
      <AnimatePresence>
        {status === "error" && (
          <m.p
            key="err"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="w-full text-xs text-red-400 mt-1 pl-1"
          >
            {errorMsg}
          </m.p>
        )}
      </AnimatePresence>
    </form>
  );
}
